import { fuerBedrock, zerlege, type Segment } from "@gui-creator/gemeinsam";
import { useLayoutEffect, useMemo, useRef } from "react";
import type { Glyph, Glyphen } from "./schrift.ts";
import { umbrechen } from "./umbruch.ts";

type Optionen = {
  text: string;
  rollen: Record<string, string>;
  glyphen: Glyphen | null;
  skala: number;
  farbe: string;
  schatten?: boolean;
  kursivStandard?: boolean;
  bedrock?: boolean;
  zentriert?: boolean;
  umbruch?: number;
  className?: string;
};

const ZEILE = 10;
// Akzente ragen bis zu drei Pixel über die Zeile hinaus.
const RAND = 3;

function schattenVon(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) >> 2;
  const g = ((n >> 8) & 255) >> 2;
  const b = (n & 255) >> 2;
  return `rgb(${r} ${g} ${b})`;
}

const getoent = new Map<string, HTMLCanvasElement>();

function glyphInFarbe(z: string, gl: Glyph, farbe: string): HTMLCanvasElement {
  const schluessel = `${z}|${farbe}|${gl.bild.src}|${gl.zb}x${gl.zh}`;
  const vorhanden = getoent.get(schluessel);
  if (vorhanden) return vorhanden;
  const c = document.createElement("canvas");
  c.width = gl.zb;
  c.height = gl.zh;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(gl.bild, gl.sx, gl.sy, gl.zb, gl.zh, 0, 0, gl.zb, gl.zh);
  ctx.globalCompositeOperation = "source-in";
  ctx.fillStyle = farbe;
  ctx.fillRect(0, 0, gl.zb, gl.zh);
  getoent.set(schluessel, c);
  return c;
}

function zeilenAus(text: string, rollen: Record<string, string>, bedrock: boolean): Segment[][] {
  return text.split("\n").map((zeile) => {
    const segmente = zerlege(zeile, rollen).segmente;
    return bedrock ? fuerBedrock(segmente) : segmente;
  });
}

function breiteVon(g: Glyphen, zeile: Segment[]): number {
  let b = 0;
  for (const s of zeile) for (const z of s.text) b += (g.zeichen.get(z)?.vorschub ?? 6) + (s.fett ? 1 : 0);
  return b;
}

/** Text in Spielschrift; ohne Schriftgrafik als gewöhnlicher Text in den Spielfarben. */
export function SpielText({ text, rollen, glyphen, skala, farbe, schatten = false, kursivStandard = false, bedrock = false, zentriert = false, umbruch, className }: Optionen) {
  const zeilen = useMemo(() => {
    const roh = zeilenAus(text, rollen, bedrock);
    if (!umbruch || !glyphen) return roh;
    return umbrechen(roh, (z, fett) => (glyphen.zeichen.get(z)?.vorschub ?? 6) + (fett ? 1 : 0), umbruch);
  }, [text, rollen, bedrock, umbruch, glyphen]);
  const leinwand = useRef<HTMLCanvasElement>(null);
  const klartext = zeilen.map((z) => z.map((s) => s.text).join("")).join("\n");

  useLayoutEffect(() => {
    const c = leinwand.current;
    if (!c || !glyphen) return;
    const breite = Math.max(1, ...zeilen.map((z) => breiteVon(glyphen, z))) + 1;
    const hoehe = zeilen.length * ZEILE + RAND;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.ceil(breite * skala * dpr);
    c.height = Math.ceil(hoehe * skala * dpr);
    c.style.width = `${breite * skala}px`;
    c.style.height = `${hoehe * skala}px`;
    c.style.marginTop = `${-RAND * skala}px`;
    const ctx = c.getContext("2d")!;
    ctx.imageSmoothingEnabled = false;
    ctx.scale(skala * dpr, skala * dpr);
    ctx.translate(0, RAND);
    const zeichne = (dx: number, nurSchatten: boolean) => {
      zeilen.forEach((zeile, y) => {
        let x = dx + (zentriert ? Math.floor((breite - 1 - breiteVon(glyphen, zeile)) / 2) : 0);
        for (const s of zeile) {
          const grund = s.farbe ?? farbe;
          const f = nurSchatten ? schattenVon(grund) : grund;
          const kursiv = s.kursiv ?? kursivStandard;
          for (const z of s.text) {
            const gl = glyphen.zeichen.get(z);
            if (gl) {
              const bild = glyphInFarbe(z, gl, f);
              const oben = y * ZEILE + (nurSchatten ? dx : 0);
              ctx.save();
              if (kursiv) ctx.transform(1, 0, -0.25, 1, 2 + oben * 0.25, 0);
              ctx.drawImage(bild, 0, 0, gl.zb, gl.zh, x, oben + gl.oben, gl.breite, gl.hoehe);
              if (s.fett) ctx.drawImage(bild, 0, 0, gl.zb, gl.zh, x + 1, oben + gl.oben, gl.breite, gl.hoehe);
              ctx.restore();
            } else if (z !== " ") {
              ctx.fillStyle = f;
              ctx.font = "7px sans-serif";
              ctx.fillText(z, x, y * ZEILE + 7);
            }
            const vorschub = (gl?.vorschub ?? 6) + (s.fett ? 1 : 0);
            ctx.fillStyle = f;
            if (s.unterstrichen) ctx.fillRect(x - 1, y * ZEILE + 8, vorschub + 1, 1);
            if (s.durchgestrichen) ctx.fillRect(x - 1, y * ZEILE + 3.5, vorschub + 1, 1);
            x += vorschub;
          }
        }
      });
    };
    if (schatten) zeichne(1, true);
    zeichne(0, false);
  }, [glyphen, zeilen, skala, farbe, schatten, kursivStandard, zentriert]);

  if (glyphen) return <canvas ref={leinwand} className={className} role="img" aria-label={klartext} />;
  return (
    <span
      className={`spieltext-ersatz ${className ?? ""}`}
      style={{ fontSize: 8 * skala, lineHeight: `${ZEILE * skala}px`, color: farbe, ...(zentriert ? { textAlign: "center" as const } : {}), ...(umbruch ? { maxWidth: umbruch * skala } : {}) }}
    >
      {zeilen.map((zeile, i) => (
        <span key={i} style={{ display: "block", minHeight: ZEILE * skala }}>
          {zeile.map((s, j) => (
            <span
              key={j}
              style={{
                color: s.farbe ?? farbe,
                fontWeight: s.fett ? 700 : undefined,
                fontStyle: (s.kursiv ?? kursivStandard) ? "italic" : undefined,
                textDecoration: [s.unterstrichen && "underline", s.durchgestrichen && "line-through"].filter(Boolean).join(" ") || undefined,
                textShadow: schatten ? `${skala}px ${skala}px 0 ${schattenVon(s.farbe ?? farbe)}` : undefined,
              }}
            >
              {s.text}
            </span>
          ))}
        </span>
      ))}
    </span>
  );
}
